import { LoadingSpinnerAnimation } from 'assets/animations';
import { JobsTabIcon, TimerIcon } from 'assets/icons';
import { GenericError, If } from 'components';
import CustomAvatar from 'components/avatar/CustomAvatar';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { usePaginatedGetJobsQuery } from 'containers/jobs/hooks/usePaginatedGetJobsQuery';
import { Assignee } from 'containers/jobs/jobs.model';
import { getJobStatusColor, getJobStatusText } from 'containers/jobs/utils';
import { useAppSelector } from 'hooks';
import Lottie from 'lottie-react';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import routesPath from 'routes/RoutesPath';
import { getAmountWithCurrency } from 'utils/amountUtils';
import { getInitials } from 'utils/commonUtils';
import DateUtils from 'utils/dateUtils';

const TodaysJobWidget = () => {
  const navigate = useNavigate();
  const currentTeam = useAppSelector(selectCurrentTeam);

  const {
    list: jobsList,
    queryParams: jobsQueryParams,
    isFetching: isLoadingJobs,
    setQueryParams: setJobQueryParams,
    isError: isJobsError,
    isLoadingSuccess: isJobsLoadingSuccess
  } = usePaginatedGetJobsQuery(1000);

  useEffect(() => {
    if (currentTeam.id) {
      const currentDate = DateUtils.getDateInFormat({ date: new Date(), dateFormat: 'yyyy-MM-dd' });
      const updatedParams = {
        ...jobsQueryParams,
        status: [],
        scheduledFrom: DateUtils.startDateToUTCString(currentDate),
        scheduledTo: DateUtils.endDateToUTCString(currentDate),
        sortBy: 'scheduled_from',
        sortOrder: 'ASC'
      };
      setJobQueryParams(updatedParams);
    }
  }, [currentTeam.id]);

  const getAssigneesWidget = (assignees: Assignee[]) => {
    if (assignees?.length > 0) {
      if (assignees.length < 3) {
        return (
          <div className="flex gap-x-2">
            {assignees.map((assignee, index) => (
              <div key={assignee.id} className="flex items-center gap-x-1">
                <CustomAvatar src={assignee.profilePictureURL} height="h-4" width="w-4" fontStyle="text-[9px]">
                  {getInitials(assignee.name)}
                </CustomAvatar>
                <div className="text-px13 text-primaryText">
                  {assignee.name}
                  {index < assignees.length - 1 && <span>,</span>}
                </div>
              </div>
            ))}
          </div>
        );
      }
      const remainingMemberCount = assignees?.length - 3;
      return (
        <div className="flex items-center">
          {assignees.map(
            (assignee, index) =>
              index < 3 && (
                <div key={assignee.id} className="-mr-1">
                  <CustomAvatar src={assignee.profilePictureURL} height="h-4" width="w-4" fontStyle="text-[9px]">
                    {getInitials(assignee.name)}
                  </CustomAvatar>
                </div>
              )
          )}
          <div className="ml-2 text-px13 text-primaryText">
            {remainingMemberCount > 0 ? `+${remainingMemberCount}` : ''}
          </div>
        </div>
      );
    }
  };

  return (
    <div className="mt-8 h-[360px] max-h-[360px] w-full rounded border border-borderGray p-5 pr-0.5 lg:w-1/2">
      <If condition={isLoadingJobs}>
        <div className="m-auto flex h-full items-center justify-center">
          <Lottie className="h-28 w-28" animationData={LoadingSpinnerAnimation} loop={true} />
        </div>
      </If>
      <If condition={!isLoadingJobs}>
        <div className="text-xl font-bold text-primaryText">Today's jobs ({jobsList?.length})</div>
        <If condition={jobsList?.length > 0}>
          <div className="customNormalScroll max-h-[300px] overflow-y-auto pt-4 pr-[18px]">
            {jobsList.map((job, index) => (
              <div
                key={job.id}
                className={`flex justify-between pt-2 pb-4 ${index > 0 && 'border-t border-secondaryBtn pt-4'} 
                          cursor-pointer`}
                role="presentation"
                onClick={() => {
                  navigate(`${routesPath.JOBS}/${currentTeam.id}/${job.id}`);
                }}>
                <div className="flex flex-col gap-y-2">
                  <div className="flex items-center gap-x-1.5">
                    <TimerIcon className="shrink-0" />
                    <div className="text-sbase text-accentText">
                      {`${DateUtils.formatTime(job.scheduledFrom)} - ${DateUtils.formatTime(job.scheduledTo)}`}
                    </div>
                  </div>
                  <div className="text-17px font-bold text-primaryText">{job.title}</div>
                  {getAssigneesWidget(job.assignees)}
                </div>
                <div className="flex flex-col items-end gap-y-2 text-right">
                  <div className="flex items-center gap-x-1.5">
                    <div
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ backgroundColor: getJobStatusColor(job.status) }}
                    />
                    <div className="text-px13 font-semibold" style={{ color: getJobStatusColor(job.status) }}>
                      {getJobStatusText(job.status)}
                    </div>
                  </div>
                  <div className="text-17px font-bold text-tertiaryText">{getAmountWithCurrency(job.amount)}</div>
                </div>
              </div>
            ))}
          </div>
        </If>
        <If condition={isJobsLoadingSuccess && jobsList?.length === 0}>
          <div className="flex h-full flex-col items-center justify-center gap-5">
            <div className="flex flex-col items-center gap-5">
              <JobsTabIcon className="h-7 w-7" />
              <div className="text-sbase text-primaryText">No jobs scheduled today</div>
            </div>
          </div>
        </If>
        <If condition={isJobsError && !isLoadingJobs && !isJobsLoadingSuccess}>
          <GenericError showBackButton={false} />
        </If>
      </If>
    </div>
  );
};

export default TodaysJobWidget;
